import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';

import { createEmployeeCreditTableConfig, TableRowAction } from './employee-credit-table.config';
import {
  CreditDetailAction,
  CreditKind,
  creditStatus,
  displayDate,
  roundMoney,
} from '../../../credit-shared/models/credit-shared.model';
import { CreditOperationsService } from '../../../credit-shared/services/credit-operations.service';
import { CreditListReadService } from '../../../credit-shared/services/credit-list-read.service';
import { CreditAttachmentDialogComponent } from '../../../credit-shared/components/credit-attachment-dialog/credit-attachment-dialog.component';
import { CreditConstitutionDialogComponent } from '../../../credit-shared/components/credit-constitution-dialog/credit-constitution-dialog.component';
import { CreditInstallmentsDialogComponent } from '../../../credit-shared/components/credit-installments-dialog/credit-installments-dialog.component';
import { CreditShortTermDebtDialogComponent } from '../../../credit-shared/components/credit-short-term-debt-dialog/credit-short-term-debt-dialog.component';
import { CreditFormDialogComponent } from '../../../credit-shared/components/credit-form-dialog/credit-form-dialog.component';

@Component({
  standalone: false,
  selector: 'app-employee-credit-list',
  templateUrl: './employee-credit-list.components.html',
  styleUrls: ['./employee-credit-list.components.css']
})
export class EmployeeCreditListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table') t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('creditSummary') creditSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  readonly kind: CreditKind = 'employee';

  tableConfig!: TableConfig;

  icon = 'group';
  title = 'Créditos con empleados';
  subTitle = 'Consulta, edita o gestiona tus créditos con empleados';

  quickSearch = '';
  loadVersion = 0;
  summary = { initial: 0, pending: 0 };

  private rows: any[] = [];
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;
  private readonly quickSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private operations: CreditOperationsService,
    private reads: CreditListReadService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private loadingDialog: LoadingDialogService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tableConfig = createEmployeeCreditTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.UploadFile]: (row) => this.uploadFileAttachment(row),
          [TableRowAction.DownloadFile]: (row) => this.downloadFileAttachment(row),
          [TableRowAction.Constitution]: (row) => this.constitution(row),
          [TableRowAction.ShortTermDebt]: (row) => this.shortTermDebt(row),
          [TableRowAction.CollectInstallments]: (row) => this.collectInstallments(row),
        },
        selectable: false,
        columns: [
          { key: 'employeesName', label: 'Empleado', sortable: true, sortKey: 'EmployeesName', ellipsis: true, onCellClick: (row: any) => this.edit(row) },
          { key: 'importeInicial', label: 'Importe inicial', sortable: true, sortKey: 'ImporteInicial', formatFn: (val: any) => Utils.FormatMoney(val ?? 0) + ' €' },
          { key: 'importePendiente', label: 'Importe pendiente', sortable: true, sortKey: 'ImportePendiente', formatFn: (val: any) => Utils.FormatMoney(val ?? 0) + ' €' },
          {
            key: 'estado', label: 'Estado', sortable: true, sortKey: 'Estado',
            badge: {
              variantFn: (_: string, row: any) => creditStatus(row).variant,
              labelFn: (_: string, row: any) => creditStatus(row).label,
            },
          },
          { key: 'fechaConstitucion', label: 'Fecha constitución', sortable: true, sortKey: 'FechaConstitucion', formatFn: (val: any) => displayDate(val) },
          { key: 'fechaFinalizacion', label: 'Fecha finalización', sortable: true, sortKey: 'FechaFinalizacion', formatFn: (val: any) => displayDate(val) },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Contabilizar (asiento libre)', icon: 'show_chart', type: 'button', callback: () => this.addManualAccountMovements() },
        ],
        rowConfig: {},
      },
      {
        filterView: '',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.creditSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = String((state.filters || {}).search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  onDetailAction(row: any, action: CreditDetailAction): void {
    if (action.type === 'edit') this.edit(row);
    if (action.type === 'changed') this.load();
    if (action.type === 'constitution') this.constitution(row, action.editUid);
    if (action.type === 'shortTermDebt') this.shortTermDebt(row, action.editUid);
    if (action.type === 'installments') this.collectInstallments(row, action.editUid);
  }

  create(): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditFormDialogComponent.open(this.dialog, { kind: this.kind })
      .subscribe(saved => { if (saved) this.load(); });
  }

  edit(row: any): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditFormDialogComponent.open(this.dialog, { kind: this.kind, uid: row.uid })
      .subscribe(saved => { if (saved) this.load(); });
  }

  delete(row: any): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar crédito',
      subtitle: row.employeesName || undefined,
      message: 'Se eliminarán el crédito, sus constituciones, deudas a corto plazo y cobros de cuotas junto con sus asientos contables. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.creditDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando crédito…',
      errorText: 'No se pudo eliminar el crédito',
      action: () => this.operations.deleteCredit(this.kind, row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Crédito eliminado correctamente');
      this.load();
    });
  }

  uploadFileAttachment(row: any): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditAttachmentDialogComponent.open(this.dialog, { kind: this.kind, credit: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  downloadFileAttachment(row: any): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loadingDialog.run('Descargando cuadro de amortización…', this.operations.downloadAttachment(this.kind, row.uid))
      .then(blob => FileSaver.saveAs(blob, Utils.GetFileName('Fichero_Adjunto', consts.EXT_EXCEL)))
      .catch(() => this.notify.error('No se pudo descargar el cuadro de amortización'));
  }

  constitution(row: any, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditConstitutionDialogComponent.open(this.dialog, { kind: this.kind, credit: row, editUid })
      .subscribe(changed => { if (changed) this.load(); });
  }

  shortTermDebt(row: any, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditShortTermDebtDialogComponent.open(this.dialog, { kind: this.kind, credit: row, editUid })
      .subscribe(changed => { if (changed) this.load(); });
  }

  collectInstallments(row: any, editUid?: string): void {
    CreditInstallmentsDialogComponent.open(this.dialog, { kind: this.kind, credit: row, editUid })
      .subscribe(changed => { if (changed) this.load(); });
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') {
        this.load();
      }
    });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list(this.kind, {
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items.map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
        this.summary = {
          initial: roundMoney(result.summary.initialAmount),
          pending: roundMoney(result.summary.outstandingAmount),
        };
        this.loadVersion++;
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de créditos con empleados');
      });
  }

  private creditDetails(row: any): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    details.push({ label: 'Importe inicial', value: Utils.FormatMoney(row.importeInicial ?? 0) + ' €' });
    details.push({ label: 'Importe pendiente', value: Utils.FormatMoney(row.importePendiente ?? 0) + ' €' });
    if (row.fechaConstitucion) details.push({ label: 'Constitución', value: displayDate(row.fechaConstitucion) });
    return details;
  }
}
